import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Header } from '../../components/ui/Header';
import Button from '../../components/ui/Button';

import SearchBar from './components/SearchBar';
import SearchResults from './components/SearchResults';
import ProductTable from './components/ProductTable';
import OrderSummary from './components/OrderSummary';
import Toast from './components/Toast';
import { sendOrderEmail, initEmailJS, validateEmailConfig } from '../../utils/emailService';
import Icon from '../../components/AppIcon';
import { fetchProductData, transformProductData } from '../../utils/googleSheetsData';
import { productFlagsService } from '../../utils/productFlagsService';
import { productSyncService } from '../../utils/productSyncService';
// ✅ Import cleanup service for initialization
import { scheduledCleanupService } from '../../services/scheduledCleanupService';

// ✅ Storage keys for persistence
const STORAGE_KEYS = {
  CART_STATE: 'order_module_cart_state',
  PRODUCT_FLAGS: 'order_module_product_flags',
  SESSION_ID: 'order_module_session_id',
  SEARCH_STATE: 'order_module_search_state',
  LAST_SAVE_TIME: 'order_module_last_save'
};

// ✅ Auto-save interval (every 30 seconds)
const AUTO_SAVE_INTERVAL = 30000;

const ProductOrderManagement = () => {
  // Product data state
  const [products, setProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [nameSearchEnabled, setNameSearchEnabled] = useState(true);
  const [skuSearchEnabled, setSkuSearchEnabled] = useState(true);
  const [quantities, setQuantities] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [toast, setToast] = useState({ message: '', type: 'info', isVisible: false });

  // Product flags state
  const [productFlags, setProductFlags] = useState({});
  const [sessionId, setSessionId] = useState(null);

  // Email configuration state
  const [emailConfig, setEmailConfig] = useState(null);

  // ✅ Add cleanup status state
  const [cleanupStatus, setCleanupStatus] = useState(null);

  // ✅ Enhanced sync status state
  const [syncStatus, setSyncStatus] = useState(null);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);
  const [autoSyncCompleted, setAutoSyncCompleted] = useState(false);

  // Debounced search
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // ✅ Add persistence state
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);

  // ✅ Add auth and navigation hooks
  const { userProfile, signOut } = useAuth();
  const navigate = useNavigate();

  // ✅ Enhanced localStorage helper functions with error handling
  const saveToStorage = useCallback((key, data) => {
    try {
      if (typeof data === 'object') {
        localStorage.setItem(key, JSON.stringify(data));
      } else {
        localStorage.setItem(key, data);
      }
      console.log(`💾 Saved ${key} to localStorage`);
      return true;
    } catch (error) {
      console.error(`❌ Failed to save ${key} to localStorage:`, error);
      showToast('⚠️ Impossibile salvare i dati localmente. Attenzione: i dati potrebbero andare persi.', 'warning');
      return false;
    }
  }, []);

  const loadFromStorage = useCallback((key, defaultValue = null) => {
    try {
      const stored = localStorage.getItem(key);
      if (stored === null) return defaultValue;
      
      // Try to parse as JSON, fallback to string
      try {
        return JSON.parse(stored);
      } catch {
        return stored;
      }
    } catch (error) {
      console.error(`❌ Failed to load ${key} from localStorage:`, error);
      return defaultValue;
    }
  }, []);

  const removeFromStorage = useCallback((key) => {
    try {
      localStorage.removeItem(key);
      console.log(`🗑️ Removed ${key} from localStorage`);
    } catch (error) {
      console.error(`❌ Failed to remove ${key} from localStorage:`, error);
    }
  }, []);

  // ✅ Auto-save cart state to localStorage
  const saveCartState = useCallback(() => {
    if (!autoSaveEnabled) return;

    const cartState = {
      quantities,
      productFlags,
      sessionId,
      searchQuery,
      nameSearchEnabled,
      skuSearchEnabled,
      timestamp: Date.now()
    };

    const saved = saveToStorage(STORAGE_KEYS?.CART_STATE, cartState);
    if (saved) {
      setLastSaveTime(new Date());
      saveToStorage(STORAGE_KEYS?.LAST_SAVE_TIME, Date.now());
    }
  }, [quantities, productFlags, sessionId, searchQuery, nameSearchEnabled, skuSearchEnabled, autoSaveEnabled, saveToStorage]);

  // ✅ Load cart state from localStorage
  const loadCartState = useCallback(() => {
    try {
      const cartState = loadFromStorage(STORAGE_KEYS?.CART_STATE);
      const lastSaveTimestamp = loadFromStorage(STORAGE_KEYS?.LAST_SAVE_TIME);
      
      if (cartState && typeof cartState === 'object') {
        // Check if the saved state is not too old (more than 24 hours)
        const now = Date.now();
        const stateAge = now - (cartState?.timestamp || 0);
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours

        if (stateAge < maxAge) {
          console.log('📥 Restoring cart state from localStorage');
          
          // Restore state with safety checks
          if (cartState?.quantities && typeof cartState?.quantities === 'object') {
            setQuantities(cartState?.quantities);
          }
          
          if (cartState?.productFlags && typeof cartState?.productFlags === 'object') {
            setProductFlags(cartState?.productFlags);
          }
          
          if (cartState?.sessionId && typeof cartState?.sessionId === 'string') {
            setSessionId(cartState?.sessionId);
          }
          
          if (typeof cartState?.searchQuery === 'string') {
            setSearchQuery(cartState?.searchQuery);
            setDebouncedSearchQuery(cartState?.searchQuery);
          }
          
          if (typeof cartState?.nameSearchEnabled === 'boolean') {
            setNameSearchEnabled(cartState?.nameSearchEnabled);
          }
          
          if (typeof cartState?.skuSearchEnabled === 'boolean') {
            setSkuSearchEnabled(cartState?.skuSearchEnabled);
          }

          if (lastSaveTimestamp) {
            setLastSaveTime(new Date(lastSaveTimestamp));
          }

          // Count items in cart
          const totalItems = Object.values(cartState?.quantities || {})?.reduce((sum, qty) => sum + (parseInt(qty) || 0), 0);
          const totalFlags = Object.values(cartState?.productFlags || {})?.filter(flag => flag)?.length;
          
          if (totalItems > 0 || totalFlags > 0) {
            showToast(`🔄 Sessione ripristinata: ${totalItems} articoli nel carrello, ${totalFlags} prodotti flaggati`, 'success', 6000);
          }
          
          return true;
        } else {
          console.log('⏰ Saved cart state is too old, clearing...');
          removeFromStorage(STORAGE_KEYS?.CART_STATE);
          removeFromStorage(STORAGE_KEYS?.LAST_SAVE_TIME);
        }
      }
    } catch (error) {
      console.error('❌ Error loading cart state:', error);
      showToast('⚠️ Errore nel ripristino della sessione precedente', 'warning');
    }
    
    return false;
  }, [loadFromStorage, removeFromStorage]);

  // ✅ Add logout handler
  const handleLogout = async () => {
    // Save current state before logout
    saveCartState();
    await signOut();
    navigate('/login');
  };

  // ✅ Enhanced handleRefreshGoogleSheets function with error recovery
  const handleRefreshGoogleSheets = useCallback(async () => {
    setIsLoadingProducts(true);
    try {
      console.log('🔄 Aggiornamento foglio Google in corso...');
      const sheetsData = await fetchProductData();
      const transformedProducts = transformProductData(sheetsData);
      
      setProducts(transformedProducts);
      showToast(`🔄 Foglio aggiornato con successo! Caricati ${transformedProducts?.length} prodotti`, 'success');
      
      // Don't reset search and quantities on refresh - preserve user work
      // Only reset if explicitly requested by user
      
    } catch (error) {
      console.error('Error refreshing Google Sheets:', error);
      showToast('❌ Errore nell\'aggiornamento del foglio Google: ' + error?.message, 'error');
      
      // Provide recovery options
      setTimeout(() => {
        showToast('💡 Suggerimento: Verifica la connessione internet e riprova. I tuoi dati sono salvati automaticamente.', 'info', 8000);
      }, 2000);
    } finally {
      setIsLoadingProducts(false);
    }
  }, []);

  // ✅ Initialize EmailJS and session with persistence
  useEffect(() => {
    // Initialize EmailJS
    initEmailJS();
    setEmailConfig(validateEmailConfig());
    
    // Try to restore existing session ID or generate new one
    let existingSessionId = loadFromStorage(STORAGE_KEYS?.SESSION_ID);
    
    if (!existingSessionId) {
      existingSessionId = productFlagsService?.generateSessionId();
      saveToStorage(STORAGE_KEYS?.SESSION_ID, existingSessionId);
    }
    
    setSessionId(existingSessionId);
    console.log('🆔 Session ID initialized:', existingSessionId);
    
    // Load previous cart state
    const wasRestored = loadCartState();
    
    // ✅ Initialize cleanup service and get status
    const initializeCleanupService = async () => {
      try {
        const statusResult = await scheduledCleanupService?.getCleanupStatus();
        if (statusResult?.success) {
          setCleanupStatus(statusResult?.status);
        }
      } catch (error) {
        console.error('Error initializing cleanup service:', error);
      }
    };
    
    initializeCleanupService();
  }, [loadFromStorage, saveToStorage, loadCartState]);

  // ✅ Enhanced debounced search with error handling
  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        setDebouncedSearchQuery(searchQuery);
        setIsSearching(false);
      } catch (error) {
        console.error('Error in search debounce:', error);
        setIsSearching(false);
        showToast('⚠️ Errore nella ricerca. Riprova.', 'warning');
      }
    }, 300);

    if (searchQuery !== debouncedSearchQuery) {
      setIsSearching(true);
    }

    return () => clearTimeout(timer);
  }, [searchQuery, debouncedSearchQuery]);

  // ✅ Auto-save cart state periodically
  useEffect(() => {
    if (!autoSaveEnabled) return;

    const autoSaveTimer = setInterval(() => {
      // Only auto-save if there's meaningful state to save
      const hasItems = Object.keys(quantities)?.length > 0;
      const hasFlags = Object.keys(productFlags)?.length > 0;
      const hasSearch = searchQuery?.trim()?.length > 0;
      
      if (hasItems || hasFlags || hasSearch) {
        saveCartState();
      }
    }, AUTO_SAVE_INTERVAL);

    return () => clearInterval(autoSaveTimer);
  }, [quantities, productFlags, searchQuery, autoSaveEnabled, saveCartState]);

  // ✅ Save state when quantities change
  useEffect(() => {
    const hasItems = Object.keys(quantities)?.some(key => quantities?.[key] > 0);
    if (hasItems) {
      // Debounce saves to avoid too frequent localStorage writes
      const saveTimer = setTimeout(() => {
        saveCartState();
      }, 1000);
      
      return () => clearTimeout(saveTimer);
    }
  }, [quantities, saveCartState]);

  // ✅ Save state when product flags change
  useEffect(() => {
    const hasFlags = Object.keys(productFlags)?.some(key => productFlags?.[key]);
    if (hasFlags) {
      const saveTimer = setTimeout(() => {
        saveCartState();
      }, 1000);
      
      return () => clearTimeout(saveTimer);
    }
  }, [productFlags, saveCartState]);

  // Load product flags when products change
  useEffect(() => {
    const loadProductFlags = async () => {
      if (products?.length > 0 && sessionId) {
        try {
          const result = await productFlagsService?.getUserProductFlags(sessionId);
          if (result?.success) {
            // Merge with existing flags from localStorage
            setProductFlags(prevFlags => ({
              ...prevFlags,
              ...(result?.data || {})
            }));
          }
        } catch (error) {
          console.error('Error loading product flags:', error);
          showToast('⚠️ Errore nel caricamento flag prodotti. Funzionalità limitata.', 'warning');
        }
      }
    };

    loadProductFlags();
  }, [products, sessionId]);

  // ✅ Enhanced products loading with automatic sync on startup
  useEffect(() => {
    const loadProductsWithAutoSync = async () => {
      setIsLoadingProducts(true);
      try {
        console.log('🚀 Caricamento prodotti dal foglio Google...');
        const sheetsData = await fetchProductData();
        const transformedProducts = transformProductData(sheetsData);
        
        setProducts(transformedProducts);
        showToast(`📄 Caricati ${transformedProducts?.length} prodotti dal foglio Google`, 'info');
        
        // ✅ NEW: Automatic synchronization on startup
        if (transformedProducts?.length > 0) {
          console.log('🔄 Avvio sincronizzazione automatica...');
          
          const autoSyncResult = await productSyncService?.autoSyncOnStartup(
            transformedProducts,
            (progress) => {
              if (progress?.phase === 'PROGRESSIVE_SYNC') {
                setSyncProgress({
                  currentBatch: progress?.currentBatch,
                  totalBatches: progress?.totalBatches,
                  percentage: progress?.percentage,
                  created: progress?.created,
                  processed: progress?.processed
                });
              } else if (progress?.phase === 'COMPLETED') {
                setAutoSyncCompleted(true);
                setSyncProgress(null);
              }
            }
          );

          if (autoSyncResult?.success) {
            if (autoSyncResult?.data?.skipped) {
              console.log('✅ Sync non necessaria - tutti i prodotti già presenti');
              showToast('✅ Tutti i prodotti sono già sincronizzati', 'success');
            } else {
              const { productsCreated, productsExisted } = autoSyncResult?.data;
              console.log(`✅ Auto-sync completata: ${productsCreated} creati, ${productsExisted} esistenti`);
              showToast(`🎉 Sincronizzazione automatica completata!\n✨ ${productsCreated} prodotti creati nel database\n📦 Ora puoi flaggare qualsiasi prodotto senza errori`, 'success', 6000);
            }
          } else {
            console.warn('⚠️ Auto-sync fallita:', autoSyncResult?.error);
            showToast(`⚠️ Sincronizzazione automatica fallita: ${autoSyncResult?.error}`, 'warning', 5000);
          }
        }
        
      } catch (error) {
        console.error('Error loading products from Google Sheets:', error);
        showToast('❌ Errore nel caricamento dal foglio Google: ' + error?.message, 'error');
        setProducts([]);
        
        // Provide recovery suggestion
        setTimeout(() => {
          showToast('💡 I tuoi dati del carrello sono comunque al sicuro e verranno ripristinati appena la connessione sarà ristabilita.', 'info', 8000);
        }, 3000);
      } finally {
        setIsLoadingProducts(false);
      }
    };

    loadProductsWithAutoSync();
  }, []);

  // ✅ Add sync check when products are loaded
  useEffect(() => {
    const checkSyncStatus = async () => {
      if (products?.length > 0) {
        try {
          const syncCheckResult = await productSyncService?.checkSyncNeeded(products);
          if (syncCheckResult?.success) {
            setSyncStatus(syncCheckResult?.data);
            
            // Show sync modal if many products are missing
            if (syncCheckResult?.data?.totalMissing > 10) {
              setShowSyncModal(true);
            }
          }

          // Also get detailed sync stats
          const statsResult = await productSyncService?.getSyncStats();
          if (statsResult?.success) {
            console.log('📊 Sync Stats:', statsResult?.data);
          }
        } catch (error) {
          console.error('Error checking sync status:', error);
        }
      }
    };

    checkSyncStatus();
  }, [products]);

  // ✅ Enhanced sync check when products are loaded
  useEffect(() => {
    const checkSyncStatus = async () => {
      if (products?.length > 0 && autoSyncCompleted) {
        try {
          // Use the new sync requirements function
          const requirementsResult = await productSyncService?.getSyncRequirements();
          if (requirementsResult?.success) {
            const requirements = requirementsResult?.data;
            setSyncStatus({
              syncNeeded: requirements?.syncNeeded,
              totalMissing: requirements?.missingProducts,
              totalFlagged: requirements?.totalFlaggedSkus,
              missingSKUs: requirements?.missingSkus,
              recommendations: requirements?.recommendations
            });
          }

          // Also get detailed sync stats for logging
          const statsResult = await productSyncService?.getSyncStats();
          if (statsResult?.success) {
            console.log('📊 Sync Stats dopo auto-sync:', statsResult?.data);
          }
        } catch (error) {
          console.error('Error checking sync status:', error);
        }
      }
    };

    // Only check after auto-sync is completed
    if (autoSyncCompleted) {
      checkSyncStatus();
    }
  }, [products, autoSyncCompleted]);

  // ✅ Enhanced product flag changes with error recovery
  const handleProductFlagChange = useCallback(async (productSku, isChecked) => {
    try {
      const result = await productFlagsService?.setProductFlag(productSku, isChecked, sessionId);
      
      if (result?.success) {
        setProductFlags(prev => ({
          ...prev,
          [productSku]: isChecked
        }));
        
        // ✅ Show success message for flagging
        showToast(`✅ Prodotto ${productSku} ${isChecked ? 'flaggato' : 'rimosso dai flag'}`, 'success', 2000);
      } else {
        // ✅ Enhanced error handling - if product not found, try instant creation
        const errorMsg = result?.error || 'Errore sconosciuto';
        const productDetails = products?.find((product) => product?.sku === productSku);
        
        if (
          errorMsg?.includes('non trovato') ||
          errorMsg?.includes('not found') ||
          errorMsg?.includes('non esiste nel sistema locale')
        ) {
          // Try instant product creation
          showToast(`🔄 Creazione automatica prodotto ${productSku}...`, 'info', 2000);
          
          const createResult = await productSyncService?.createProductInstant(
            productSku,
            productDetails?.name || `Prodotto ${productSku}`,
            parseFloat(productDetails?.publicPrice || productDetails?.price || 0),
            parseFloat(productDetails?.resellerPrice || productDetails?.price || 0),
            productDetails?.category || 'other'
          );
          
          if (createResult?.success) {
            showToast(`✅ Prodotto ${productSku} creato e flaggato automaticamente!`, 'success');
            
            // Retry the flagging operation
            const retryResult = await productFlagsService?.setProductFlag(productSku, isChecked, sessionId);
            if (retryResult?.success) {
              setProductFlags(prev => ({
                ...prev,
                [productSku]: isChecked
              }));
            }
          } else {
            showToast(`❌ Impossibile creare automaticamente il prodotto ${productSku}: ${createResult?.error}`, 'error');
          }
        } else {
          showToast(`❌ Errore nel flag prodotto: ${errorMsg}`, 'error');
        }
      }
    } catch (error) {
      console.error('Error updating product flag:', error);
      showToast('❌ Errore imprevisto durante il salvataggio del flag. I tuoi dati sono comunque al sicuro.', 'error');
      
      // Even if network call failed, update local state to provide immediate feedback
      setProductFlags(prev => ({
        ...prev,
        [productSku]: isChecked
      }));
    }
  }, [sessionId]);

  // ✅ Add function to clear flags after order completion
  const handleClearOrderFlags = useCallback(async () => {
    try {
      const result = await productFlagsService?.clearAllFlags(sessionId);
      if (result?.success) {
        setProductFlags({});
        // Move showToast call to avoid circular dependency
        setToast({ message: 'Flag prodotti azzerati per nuovo ordine', type: 'success', isVisible: true });
      } else {
        setToast({ message: 'Errore nell\'azzeramento flag: ' + result?.error, type: 'error', isVisible: true });
      }
    } catch (error) {
      console.error('Error clearing product flags:', error);
      setToast({ message: 'Errore nell\'azzeramento flag prodotti', type: 'error', isVisible: true });
    }
  }, [sessionId]);

  // ✅ Enhanced new order function with confirmation
  const handleStartNewOrder = useCallback(async () => {
    // Check if there's significant work to lose
    const totalItems = Object.values(quantities)?.reduce((sum, qty) => sum + (parseInt(qty) || 0), 0);
    const totalFlags = Object.values(productFlags)?.filter(flag => flag)?.length;
    
    if (totalItems > 5 || totalFlags > 3) {
      const confirmed = window.confirm(
        `⚠️ ATTENZIONE: Stai per perdere tutto il lavoro fatto!\n\n` +
        `• ${totalItems} articoli nel carrello\n` +
        `• ${totalFlags} prodotti flaggati\n\n` +
        `Sei sicuro di voler iniziare un nuovo ordine?`
      );
      
      if (!confirmed) {
        showToast('✅ Operazione annullata - i tuoi dati sono al sicuro', 'info');
        return;
      }
    }
    
    // Clear quantities
    setQuantities({});
    
    // Clear flags
    await handleClearOrderFlags();
    
    // Clear localStorage
    removeFromStorage(STORAGE_KEYS?.CART_STATE);
    removeFromStorage(STORAGE_KEYS?.LAST_SAVE_TIME);
    
    setToast({ message: 'Nuovo ordine iniziato - carrello e flag azzerati', type: 'success', isVisible: true });
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [quantities, productFlags, handleClearOrderFlags, removeFromStorage]);

  // ✅ Add sync products handler
  const handleSyncProducts = useCallback(async () => {
    if (!products?.length) {
      showToast('Nessun prodotto da sincronizzare', 'warning');
      return;
    }

    setSyncInProgress(true);
    setShowSyncModal(false);

    try {
      showToast('🔄 Sincronizzazione prodotti in corso...', 'info');

      const syncResult = await productSyncService?.syncExternalProducts(products);
      
      if (syncResult?.success) {
        const { productsCreated, productsExisted, totalProcessed } = syncResult?.data;
        
        showToast(
          `✅ Sincronizzazione completata!\n📦 ${totalProcessed} prodotti processati\n🆕 ${productsCreated} creati\n✓ ${productsExisted} già esistenti`, 
          'success',
          6000
        );

        // Update sync status
        const newSyncCheck = await productSyncService?.checkSyncNeeded(products);
        if (newSyncCheck?.success) {
          setSyncStatus(newSyncCheck?.data);
        }
        
      } else {
        showToast(`❌ Errore nella sincronizzazione: ${syncResult?.error}`, 'error');
      }
    } catch (error) {
      console.error('Error syncing products:', error);
      showToast('❌ Errore imprevisto durante la sincronizzazione', 'error');
    } finally {
      setSyncInProgress(false);
    }
  }, [products]);

  // ✅ Add function to handle manual sync for specific product
  const handleSyncSingleProduct = useCallback(async (productSku) => {
    const product = products?.find(p => p?.sku === productSku);
    if (!product) return;

    try {
      showToast(`🔄 Sincronizzazione prodotto ${productSku}...`, 'info');

      const result = await productSyncService?.validateAndCreateProduct(
        product?.sku,
        product?.name,
        parseFloat(product?.publicPrice || 0),
        parseFloat(product?.resellerPrice || 0),
        product?.category || 'other'
      );

      if (result?.success) {
        if (result?.data?.wasCreated) {
          showToast(`✅ Prodotto ${productSku} creato nel database`, 'success');
        } else {
          showToast(`ℹ️ Prodotto ${productSku} già presente nel database`, 'info');
        }
      } else {
        showToast(`❌ Errore sincronizzazione ${productSku}: ${result?.error}`, 'error');
      }
    } catch (error) {
      console.error('Error syncing single product:', error);
      showToast(`❌ Errore imprevisto sincronizzando ${productSku}`, 'error');
    }
  }, [products]);

  // ✅ Enhanced sync products handler with progress tracking
  const handleSyncProductsProgressive = useCallback(async () => {
    if (!products?.length) {
      showToast('Nessun prodotto da sincronizzare', 'warning');
      return;
    }

    setSyncInProgress(true);
    setShowSyncModal(false);
    setSyncProgress({ currentBatch: 0, totalBatches: 0, percentage: 0 });

    try {
      showToast('🔄 Sincronizzazione avanzata in corso...', 'info');

      const syncResult = await productSyncService?.syncExternalProductsProgressive(
        products,
        (progress) => {
          setSyncProgress({
            currentBatch: progress?.currentBatch || 0,
            totalBatches: progress?.totalBatches || 0,
            percentage: progress?.percentage || 0,
            processed: progress?.processed || 0,
            created: progress?.created || 0,
            existed: progress?.existed || 0
          });
        }
      );
      
      if (syncResult?.success) {
        const { productsCreated, productsExisted, totalProcessed, batchesProcessed } = syncResult?.data;
        
        showToast(
          `🎉 Sincronizzazione completata con successo!\n📦 ${totalProcessed} prodotti processati in ${batchesProcessed} batch\n🆕 ${productsCreated} nuovi prodotti creati\n✅ ${productsExisted} prodotti già esistenti\n🏁 Ora puoi flaggare tutti i prodotti senza errori!`, 
          'success',
          8000
        );

        // Update sync status
        const newRequirements = await productSyncService?.getSyncRequirements();
        if (newRequirements?.success) {
          setSyncStatus({
            syncNeeded: newRequirements?.data?.syncNeeded,
            totalMissing: newRequirements?.data?.missingProducts,
            totalFlagged: newRequirements?.data?.totalFlaggedSkus
          });
        }
        
      } else {
        showToast(`❌ Errore nella sincronizzazione: ${syncResult?.error}`, 'error');
      }
    } catch (error) {
      console.error('Error syncing products:', error);
      showToast('❌ Errore imprevisto durante la sincronizzazione', 'error');
    } finally {
      setSyncInProgress(false);
      setSyncProgress(null);
    }
  }, [products]);

  // Filter products based on search criteria
  const filteredProducts = useMemo(() => {
    if (!debouncedSearchQuery?.trim()) {
      return products;
    }

    const query = debouncedSearchQuery?.toLowerCase()?.trim();
    
    return products?.filter(product => {
      let matches = false;

      if (nameSearchEnabled) {
        const productName = product?.name?.toLowerCase();
        
        // First, try exact phrase matching (preserving spaces)
        if (productName?.includes(query)) {
          matches = true;
        } else {
          // If exact phrase doesn't match, try word-based matching
          // but be more restrictive - all words must be present as whole words or at word boundaries
          const queryWords = query?.split(/\s+/);
          const nameMatches = queryWords?.every(word => {
            // Check if word appears as a whole word or at the beginning of a word
            const wordRegex = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i');
            return wordRegex?.test(productName);
          });
          matches = matches || nameMatches;
        }
      }

      if (skuSearchEnabled) {
        const productSku = product?.sku?.toLowerCase();
        // For SKU, try exact match first, then starts with, then contains
        const skuMatches = productSku === query || 
                          productSku?.startsWith(query) ||
                          productSku?.includes(query);
        matches = matches || skuMatches;
      }

      return matches;
    });
  }, [debouncedSearchQuery, nameSearchEnabled, skuSearchEnabled, products]);

  // ✅ Enhanced search change handler with error recovery
  const handleSearchChange = useCallback((value) => {
    try {
      setSearchQuery(value || '');
    } catch (error) {
      console.error('Error in search change:', error);
      showToast('⚠️ Errore nella ricerca. La funzionalità rimane attiva.', 'warning');
      // Reset to safe state
      setSearchQuery('');
      setDebouncedSearchQuery('');
    }
  }, []);

  // ✅ Enhanced quantity change handler with validation
  const handleQuantityChange = useCallback((sku, quantity) => {
    try {
      const numQuantity = parseInt(quantity) || 0;
      
      if (numQuantity < 0) {
        showToast('⚠️ La quantità non può essere negativa', 'warning');
        return;
      }
      
      if (numQuantity > 9999) {
        showToast('⚠️ Quantità troppo elevata (max 9999)', 'warning');
        return;
      }
      
      setQuantities(prev => ({
        ...prev,
        [sku]: numQuantity
      }));
    } catch (error) {
      console.error('Error in quantity change:', error);
      showToast('⚠️ Errore nell\'aggiornamento quantità', 'warning');
    }
  }, []);

  // ✅ Enhanced clear filters with confirmation for complex searches
  const handleClearFilters = useCallback(() => {
    try {
      const isComplexSearch = searchQuery?.length > 10 || 
                             searchQuery?.includes(' ') ||
                             filteredProducts?.length < products?.length * 0.1;
      
      if (isComplexSearch && searchQuery?.trim()) {
        const confirmed = window.confirm(
          'Sei sicuro di voler cancellare questa ricerca?\n\n' +
          `Ricerca attuale: "${searchQuery}"\n` +
          `Risultati: ${filteredProducts?.length} prodotti`
        );
        
        if (!confirmed) {
          return;
        }
      }
      
      setSearchQuery('');
      setDebouncedSearchQuery('');
      setNameSearchEnabled(true);
      setSkuSearchEnabled(true);
      setIsSearching(false);
      
      setToast({ message: 'Filtri cancellati con successo', type: 'success', isVisible: true });
      
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error clearing filters:', error);
      showToast('⚠️ Errore nella cancellazione filtri', 'warning');
    }
  }, [searchQuery, filteredProducts?.length, products?.length]);

  const handleScrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const showToast = useCallback((message, type = 'info', duration = 4000) => {
    try {
      setToast({ message, type, isVisible: true, duration });
    } catch (error) {
      console.error('Error showing toast:', error);
    }
  }, []);

  const hideToast = useCallback(() => {
    setToast(prev => ({ ...prev, isVisible: false }));
  }, []);

  const handleExportOrder = useCallback(() => {
    try {
      const orderedItems = Object.entries(quantities)?.filter(([sku, qty]) => qty > 0)?.map(([sku, qty]) => {
          const product = products?.find(p => p?.sku === sku);
          const isFlagged = productFlags?.[sku] || false;
          return `${sku},${product?.name || 'N/A'},${qty},${product?.resellerPrice || product?.price || '0.00'},${isFlagged ? 'Flagged' : 'Not Flagged'}`;
        });

      if (orderedItems?.length === 0) {
        setToast({ message: 'Nessun articolo da esportare', type: 'warning', isVisible: true });
        return;
      }

      const csvContent = `SKU,Nome Prodotto,Quantità,Prezzo Rivenditore,Flag\n${orderedItems?.join('\n')}`;
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL?.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ordine_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
      link?.click();
      window.URL?.revokeObjectURL(url);

      setToast({ message: 'Ordine esportato con successo', type: 'success', isVisible: true });
    } catch (error) {
      console.error('Error exporting order:', error);
      showToast('❌ Errore nell\'esportazione. Riprova.', 'error');
    }
  }, [quantities, products, productFlags]);

  const handlePrintOrder = useCallback(() => {
    try {
      const orderedItems = Object.entries(quantities)?.filter(([sku, qty]) => qty > 0);

      if (orderedItems?.length === 0) {
        setToast({ message: 'Nessun articolo da stampare', type: 'warning', isVisible: true });
        return;
      }

      window.print();
      setToast({ message: 'Stampa avviata', type: 'success', isVisible: true });
    } catch (error) {
      console.error('Error printing order:', error);
      showToast('❌ Errore nella stampa. Riprova.', 'error');
    }
  }, [quantities]);

  const handleSendOrderEmail = useCallback(async () => {
    try {
      const orderedItems = Object.entries(quantities)?.filter(([sku, qty]) => qty > 0)?.map(([sku, qty]) => {
        const product = products?.find(p => p?.sku === sku);
        return {
          sku,
          name: product?.name || 'Prodotto non trovato',
          quantity: parseInt(qty),
          price: product?.resellerPrice || product?.price || '0.00',
          isFlagged: productFlags?.[sku] || false
        };
      });

      if (orderedItems?.length === 0) {
        setToast({ message: 'Nessun articolo da inviare', type: 'warning', isVisible: true });
        return;
      }

      const totalItems = orderedItems?.reduce((sum, item) => sum + item?.quantity, 0);

      setToast({ message: 'Invio email in corso...', type: 'info', isVisible: true });
      
      const result = await sendOrderEmail({
        items: orderedItems,
        totalItems,
        customerInfo: {
          name: 'Cliente Online',
          email: 'ordine@online.com'
        }
      });

      if (result?.success) {
        if (result?.fallback) {
          setToast({ message: 'Email client aperto - completa l\'invio manualmente', type: 'info', isVisible: true });
        } else {
          setToast({ message: 'Ordine inviato con successo', type: 'success', isVisible: true });
        }
      } else {
        throw new Error('Invio fallito');
      }
    } catch (error) {
      console.error('Email send error:', error);
      setToast({ message: 'Errore nell\'invio email - riprova più tardi', type: 'error', isVisible: true });
    }
  }, [quantities, products, productFlags]);

  // Show search results toast
  useEffect(() => {
    if (debouncedSearchQuery?.trim() && !isSearching && products?.length > 0) {
      const count = filteredProducts?.length;
      if (count === 0) {
        setToast({ message: 'Nessun prodotto trovato per la ricerca corrente', type: 'warning', isVisible: true });
      } else {
        setToast({ message: `Trovati ${count} prodotti`, type: 'success', isVisible: true });
      }
    }
  }, [filteredProducts?.length, debouncedSearchQuery, isSearching, products?.length]);

  return (
    <>
      <Helmet>
        <title>Sistema Ordini</title>
        <meta name="description" content="Sistema di ordinazione prodotti" />
      </Helmet>
      <div className="min-h-screen overflow-x-clip bg-background">
        {/* ✅ Updated Header with proper authentication props */}
        <Header 
          title="Sistema Ordini"
          subtitle="Gestione ordini prodotti"
          userProfile={userProfile}
          onLogout={handleLogout}
          actions={
            <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
              {/* ✅ Show admin actions only if user is admin */}
              {userProfile?.role === 'admin' && (
                <>
                  <button
                    onClick={() => navigate('/statistics-dashboard')}
                    className="flex w-full items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:w-auto"
                  >
                    <Icon name="BarChart3" className="w-4 h-4 mr-2" />
                    Statistiche
                  </button>
                  <button
                    onClick={() => navigate('/user-management-dashboard')}
                    className="flex w-full items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 sm:w-auto"
                  >
                    <Icon name="Users" className="w-4 h-4 mr-2" />
                    Utenti
                  </button>
                </>
              )}
            </div>
          }
        />
        
        <main className="pt-16">
          <div className="container mx-auto max-w-7xl px-4 py-6 sm:py-8">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                Sistema Ordini
              </h1>
            </div>

            {/* Loading State */}
            {isLoadingProducts && (
              <div className="mb-8 text-center">
                <div className="bg-card border border-border rounded-lg p-8 max-w-md mx-auto">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Caricamento e sincronizzazione automatica...</p>
                  {syncProgress && (
                    <div className="mt-4">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                          style={{ width: `${syncProgress?.percentage || 0}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-600 mt-2">
                        Batch {syncProgress?.currentBatch}/{syncProgress?.totalBatches} - {syncProgress?.created || 0} prodotti creati
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ✅ Enhanced Auto-Save Status */}
            {lastSaveTime && !isLoadingProducts && (
              <div className="mb-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start space-x-2">
                      <Icon name="Shield" className="text-green-600" size={16} />
                      <div>
                        <p className="text-xs font-medium text-green-800">
                          🛡️ I tuoi dati sono protetti - Ultimo salvataggio: {lastSaveTime?.toLocaleTimeString()}
                        </p>
                        <p className="text-xs text-green-700">
                          Carrello e flag vengono salvati automaticamente ogni 30 secondi. 
                          Anche in caso di chiusura accidentale, il tuo lavoro è al sicuro.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 self-start sm:self-auto">
                      <label className="flex items-center space-x-1">
                        <input
                          type="checkbox"
                          checked={autoSaveEnabled}
                          onChange={(e) => setAutoSaveEnabled(e?.target?.checked)}
                          className="w-3 h-3 text-green-600"
                        />
                        <span className="text-xs text-green-700">Auto-save</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ✅ Connection Info and Refresh - Updated for Google Sheets */}
            {products?.length > 0 && !isLoadingProducts && (
              <div className="mb-6 flex flex-col gap-4 bg-card border border-border rounded-lg p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start space-x-3">
                  <Icon name="FileSpreadsheet" className="text-green-600" />
                  <div>
                    <p className="text-sm font-medium">✅ Collegato al foglio Google</p>
                    <p className="text-xs text-muted-foreground">
                      {products?.length} prodotti caricati e sincronizzati automaticamente
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={handleRefreshGoogleSheets}
                  iconName="RefreshCw"
                  iconPosition="left"
                  disabled={isLoadingProducts}
                >
                  Aggiorna Foglio
                </Button>
              </div>
            )}

            {/* ✅ Error state - Updated for Google Sheets */}
            {!isLoadingProducts && products?.length === 0 && (
              <div className="mb-8 text-center">
                <div className="bg-card border border-border rounded-lg p-8 max-w-md mx-auto">
                  <Icon name="AlertTriangle" size={48} className="text-yellow-600 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold mb-4">Problemi di connessione al foglio Google</h2>
                  <p className="text-muted-foreground mb-6">
                    Impossibile caricare i prodotti dal foglio Google.
                    Verifica che il foglio sia pubblico e che l'URL sia corretto nella variabile VITE_GOOGLE_SHEETS_URL.
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800">
                      💡 <strong>Tranquillo!</strong> I tuoi dati del carrello sono al sicuro e verranno ripristinati 
                      appena la connessione sarà ristabilita.
                    </p>
                  </div>
                  <Button
                    variant="default"
                    onClick={handleRefreshGoogleSheets}
                    iconName="RotateCcw"
                    iconPosition="left"
                  >
                    Riprova Caricamento
                  </Button>
                </div>
              </div>
            )}

            {/* ✅ Add Storage Health Status (optional - only show if cleanup status available) */}
            {cleanupStatus?.storageStats && (
              <div className="mb-6">
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start space-x-3">
                      <Icon 
                        name={cleanupStatus?.storageStats?.storageHealth?.includes('Excellent') ? "CheckCircle" : 
                              cleanupStatus?.storageStats?.storageHealth?.includes('Good') ? "Info" : "AlertTriangle"}
                        className={cleanupStatus?.storageStats?.storageHealth?.includes('Excellent') ? "text-green-600" : 
                                  cleanupStatus?.storageStats?.storageHealth?.includes('Good') ? "text-blue-600" : "text-yellow-600"} 
                      />
                      <div>
                        <p className="text-sm font-medium">
                          Storage PDF: {cleanupStatus?.storageStats?.storageHealth}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {cleanupStatus?.storageStats?.totalFiles} file totali ({cleanupStatus?.storageStats?.totalSizeMB} MB) • 
                          {cleanupStatus?.storageStats?.filesOlderThan20Days} file verranno eliminati automaticamente
                        </p>
                      </div>
                    </div>
                    {cleanupStatus?.storageStats?.filesOlderThan20Days > 0 && (
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                        Cleanup: {cleanupStatus?.storageStats?.cleanupSavingsMB} MB recuperabili
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ✅ Automatic Sync Success Banner */}
            {autoSyncCompleted && syncStatus && !syncStatus?.syncNeeded && (
              <div className="mb-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="CheckCircle" className="text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-green-800">
                        🎉 Sincronizzazione automatica completata!
                      </p>
                      <p className="text-xs text-green-700">
                        Tutti i prodotti sono pronti per essere flaggati. Non dovrai più vedere errori di "prodotto non trovato".
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ✅ Manual Sync Status Banner - only show if needed after auto-sync */}
            {syncStatus?.syncNeeded && !syncInProgress && autoSyncCompleted && (
              <div className="mb-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Icon name="AlertTriangle" className="text-yellow-600" />
                      <div>
                        <p className="text-sm font-medium text-yellow-800">
                          ⚠️ Alcuni prodotti potrebbero non essere sincronizzati
                        </p>
                        <p className="text-xs text-yellow-700">
                          Se riscontri ancora errori di flagging, esegui una sincronizzazione manuale
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleSyncProducts}
                      iconName="RefreshCw"
                      iconPosition="left"
                      className="w-full border-yellow-300 text-yellow-700 hover:bg-yellow-100 sm:w-auto"
                      disabled={syncInProgress}
                    >
                      Sincronizza Manualmente
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* ✅ Enhanced Sync Progress Banner */}
            {syncInProgress && (
              <div className="mb-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-800">
                        🔄 Sincronizzazione avanzata in corso...
                      </p>
                      <p className="text-xs text-blue-700">
                        Processando prodotti in batch per evitare timeout. Batch {syncProgress?.currentBatch}/{syncProgress?.totalBatches}
                      </p>
                      {syncProgress && (
                        <div className="mt-2">
                          <div className="w-full bg-blue-100 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                              style={{ width: `${syncProgress?.percentage || 0}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-blue-600 mt-1">
                            {syncProgress?.processed || 0} processati • {syncProgress?.created || 0} creati • {Math.round(syncProgress?.percentage || 0)}% completato
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Main Content - Only show when products are loaded */}
            {products?.length > 0 && !isLoadingProducts && (
              <>
                {/* Search Section */}
                <div className="mb-6">
                  <SearchBar
                    searchQuery={searchQuery}
                    onSearchChange={handleSearchChange}
                    nameSearchEnabled={nameSearchEnabled}
                    skuSearchEnabled={skuSearchEnabled}
                    onNameSearchToggle={setNameSearchEnabled}
                    onSkuSearchToggle={setSkuSearchEnabled}
                    onClearFilters={handleClearFilters}
                    isSearching={isSearching}
                  />
                </div>

                {/* ✅ Enhanced "New Order" button with better warnings */}
                {Object.values(quantities)?.some(qty => qty > 0) && (
                  <div className="mb-6">
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-start space-x-3">
                          <Icon name="AlertTriangle" className="text-orange-600" />
                          <div>
                            <p className="text-sm font-medium text-orange-800">
                              ⚠️ Hai {Object.values(quantities)?.reduce((sum, qty) => sum + (parseInt(qty) || 0), 0)} articoli nel carrello
                            </p>
                            <p className="text-xs text-orange-700">
                              I tuoi dati sono salvati automaticamente. Clicca "Nuovo Ordine" solo se vuoi azzerare tutto.
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          onClick={handleStartNewOrder}
                          iconName="RotateCcw"
                          iconPosition="left"
                          className="w-full border-orange-300 text-orange-700 hover:bg-orange-100 sm:w-auto"
                        >
                          Nuovo Ordine
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Search Results Summary */}
                <div className="mb-6">
                  <SearchResults
                    totalProducts={products?.length}
                    filteredCount={filteredProducts?.length}
                    searchQuery={debouncedSearchQuery}
                  />
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                  {/* Product Table */}
                  <div className="xl:col-span-2">
                    <ProductTable
                      products={filteredProducts}
                      quantities={quantities}
                      onQuantityChange={handleQuantityChange}
                      searchQuery={debouncedSearchQuery}
                      onScrollToTop={handleScrollToTop}
                      productFlags={productFlags}
                      onProductFlagChange={handleProductFlagChange}
                    />
                  </div>

                  {/* Order Summary Sidebar */}
                  <div className="xl:col-span-1">
                    <div className="sticky top-24">
                      <OrderSummary
                        quantities={quantities}
                        products={products}
                        onQuantityChange={handleQuantityChange}
                        onExportOrder={handleExportOrder}
                        onPrintOrder={handlePrintOrder}
                        showToast={showToast}
                        productFlags={productFlags}
                        onProductFlagChange={handleProductFlagChange}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </main>

        {/* Toast Notifications */}
        <Toast
          message={toast?.message}
          type={toast?.type}
          isVisible={toast?.isVisible}
          onClose={hideToast}
          duration={toast?.duration || 4000}
        />
      </div>
      {/* Print Styles */}
      <style jsx>{`
        @media print {
          .no-print {
            display: none !important;
          }
          
          body {
            font-size: 12px;
          }
          
          .container {
            max-width: none;
            padding: 0;
          }
          
          h1:before {
            content: "Sistema Ordini - Ordine del " attr(data-print-date);
            display: block;
            font-size: 14px;
            margin-bottom: 10px;
          }
        }
      `}</style>
    </>
  );
};

export default ProductOrderManagement;
